import React, { useCallback, useEffect, useRef, useState } from 'react'
import { InputField, Modal, Select } from '@enamelai/design-system'
import { buildTimeOptionsAmPm } from '@utils/phoneManagement.utils'
import { AddHolidayFormData } from '../openingHours.data'
import ModalDateField from '../ModalDateField.component'
import '../AddOverride/AddOverrideStyle.css'

const TIME_OPTIONS = buildTimeOptionsAmPm(30).map((time) => ({
  value: time,
  label: time,
}))

const HOLIDAY_TYPE_OPTIONS = [
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
]

const emptyForm = (): AddHolidayFormData => ({
  description: '',
  date: '',
  holidayType: 'public',
  startTime: '8:00 AM',
  endTime: '6:00 PM',
})

type AddHolidayProps = {
  isOpen: boolean
  mode?: 'add' | 'edit'
  initialValues?: AddHolidayFormData | null
  onRequestClose: () => void
  onSubmit?: (data: AddHolidayFormData) => void | Promise<void>
  isSubmitting?: boolean
}

const AddHoliday: React.FC<AddHolidayProps> = ({
  isOpen,
  mode = 'add',
  initialValues = null,
  onRequestClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<AddHolidayFormData>(emptyForm())
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})
  const wasModalOpenRef = useRef(false)

  useEffect(() => {
    const justOpened = isOpen && !wasModalOpenRef.current
    wasModalOpenRef.current = isOpen
    if (!justOpened) return
    setFormData(initialValues ? { ...initialValues } : emptyForm())
    setValidationErrors({})
  }, [isOpen, initialValues])

  const handleInputChange = useCallback(
    <K extends keyof AddHolidayFormData>(
      field: K,
      value: AddHolidayFormData[K],
    ): void => {
      setFormData((prev) => ({ ...prev, [field]: value }))
      setValidationErrors((prev) => {
        if (!prev[field]) return prev
        const next = { ...prev }
        delete next[field]
        return next
      })
    },
    [],
  )

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {}
    if (!formData.description.trim()) {
      errors.description = 'Description is required'
    }
    if (!formData.date.trim()) {
      errors.date = 'Date is required'
    }
    if (!formData.startTime.trim()) {
      errors.startTime = 'Start time is required'
    }
    if (!formData.endTime.trim()) {
      errors.endTime = 'End time is required'
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [formData])

  const handleClose = useCallback((): void => {
    setFormData(emptyForm())
    setValidationErrors({})
    onRequestClose()
  }, [onRequestClose])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!validate()) return

    const payload: AddHolidayFormData = {
      description: formData.description.trim(),
      date: formData.date,
      holidayType: formData.holidayType,
      startTime: formData.startTime,
      endTime: formData.endTime,
    }

    try {
      await onSubmit?.(payload)
      handleClose()
    } catch {
      // Parent shows error toast; keep modal open for corrections.
    }
  }, [formData, handleClose, onSubmit, validate])

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      title={mode === 'edit' ? 'Edit holiday' : 'Add holiday'}
      closeOnBackdrop={false}
      className="oh-modal"
      primaryLabel={mode === 'edit' ? 'Save' : 'Add'}
      primaryDisabled={isSubmitting}
      onPrimaryClick={() => {
        void handleSave()
      }}
      secondaryLabel="Cancel"
      onSecondaryClick={handleClose}
    >
      <div className="oh-modal-form">
        <InputField
          fullWidth
          variant="text"
          label="Description"
          placeholder="e.g. New Year's Day"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          error={!!validationErrors.description}
          helperText={validationErrors.description}
        />
        <ModalDateField
          label="Date"
          value={formData.date}
          onChange={(value) => handleInputChange('date', value)}
          error={validationErrors.date}
        />
        <Select
          label="Holiday type"
          options={HOLIDAY_TYPE_OPTIONS}
          value={formData.holidayType}
          onChange={(value) =>
            handleInputChange(
              'holidayType',
              String(value) as AddHolidayFormData['holidayType'],
            )
          }
        />
        <div className="oh-modal-row">
          <Select
            label="Day-off start"
            options={TIME_OPTIONS}
            value={formData.startTime}
            error={!!validationErrors.startTime}
            helperText={validationErrors.startTime}
            onChange={(value) => handleInputChange('startTime', String(value))}
          />
          <Select
            label="Day-off end"
            options={TIME_OPTIONS}
            value={formData.endTime}
            error={!!validationErrors.endTime}
            helperText={validationErrors.endTime}
            onChange={(value) => handleInputChange('endTime', String(value))}
          />
        </div>
      </div>
    </Modal>
  )
}

export default AddHoliday
